import type {
	FetchedSelectFilterOption,
	SelectFilterState,
} from '../../../../../common/constants/filters/select-filter/types';
import {
	ProductKeys,
	type ProductKeys1P,
	type ProductKeys3P,
} from '../../../../../common/constants/products';
import { LastModified } from '../../../../../common/constants/schemas/query-params';
import { getProducts3PFromConfigForFilter } from '../../../../../common/utils/filters';
import { createSingleWithCustomOptionSelectFilter } from '../../../../filters/single-select-with-custom-option-filter';

import { lastUpdatedLabels } from './messages';
import { customValueParser, generateQueryParamsGetState } from './utils';

const LAST_UPDATED_VALUES = [
	'today',
	'yesterday',
	'past7Days',
	'past30Days',
	'pastYear',
	'custom',
] as const;

type LastUpdatedType = (typeof LAST_UPDATED_VALUES)[number];

const sortCompareFn = (a: FetchedSelectFilterOption, b: FetchedSelectFilterOption) => {
	return (
		LAST_UPDATED_VALUES.indexOf(a.value as LastUpdatedType) -
		LAST_UPDATED_VALUES.indexOf(b.value as LastUpdatedType)
	);
};

const productKeys1P: ProductKeys1P[] = [
	ProductKeys.Atlas,
	ProductKeys.Confluence,
	ProductKeys.Focus,
	ProductKeys.Jira,
	ProductKeys.JiraProductDiscovery,
	ProductKeys.JiraServiceManagement,
	ProductKeys.Compass,
	ProductKeys.Trello,
	ProductKeys.Bitbucket,
];

const products3PFromConfig: ProductKeys3P[] = getProducts3PFromConfigForFilter('lastUpdated');

export const filter: Readonly<{
	id: 'lastUpdated';
	type: 'single-select' | 'multi-select';
	queryParams: {
		key: string;
		getState: (
			queryParams: Record<string, string[]>,
			cloudId: string,
		) => SelectFilterState<'lastUpdated'>;
	};
	products: ProductKeys[];
	universal: boolean;
	customValueFields?: string[];
	withState: (
		state: unknown,
		config: import('../../../../../common/constants/filters/cloud-config/types').CloudConfig,
	) => import('../../../../../common/constants/filters/select-filter/types').SelectFilterMonad<'lastUpdated'>;
	getInitialState: (
		config: import('../../../../../common/constants/filters/cloud-config/types').CloudConfig,
	) => import('../../../../../common/constants/filters/select-filter/types').SelectFilterMonad<'lastUpdated'>;
	isCompatible: (filterId?: string) => boolean;
	isSupportCustomValue: boolean;
	isSuggestedFilter?: (primaryProduct?: string) => boolean;
}> = createSingleWithCustomOptionSelectFilter({
	id: 'lastUpdated',
	products: [...productKeys1P, ...products3PFromConfig],
	alwaysVisible: true,
	universal: true,
	queryParams: {
		key: 'lastModified',
		fetcher: async (_, config) => {
			return LAST_UPDATED_VALUES.map((value) => ({
				trackingKey: value,
				label:
					config.intl?.formatMessage(lastUpdatedLabels[value]) ||
					lastUpdatedLabels[value].defaultMessage,
				value,
				queryParamValue: value,
				product: config.product,
			}));
		},
		customValueParser,
		generateQueryParamsGetState,
	},
	initialOptions: {
		fetcher: async (config) => {
			return [
				{
					trackingKey: 'anytime',
					label:
						config.intl?.formatMessage(lastUpdatedLabels['anytime']) ||
						lastUpdatedLabels['anytime'].defaultMessage,
					value: 'anytime',
					isDefault: true,
					queryParamValue: LastModified.Anytime,
					product: config.product,
				},
				...LAST_UPDATED_VALUES.map((value) => ({
					trackingKey: value,
					label:
						config.intl?.formatMessage(lastUpdatedLabels[value]) ||
						lastUpdatedLabels[value].defaultMessage,
					value,
					queryParamValue: value,
					product: config.product,
				})),
			];
		},
	},
	sortCompareFn,
	customValueFields: ['to', 'from'],
});
